<template>
  <div class="space-y-6">
    <div class="space-y-4">
      <h2>{{ t('commissionPercentageOptions') }}</h2>
      <p class="text-sm text-gray-600">{{ t('commissionPercentageOptionsDescription') }}</p>
      <MultipleValuesListField
        name="commissionPercentageOptions"
        :button-text="t('addCommissionOption')"
        :field-settings="percentageFieldSettings"
        show-delete-alert
      />
    </div>

    <Divider />

    <div class="space-y-4">
      <h2>{{ t('discountPercentageOptions') }}</h2>
      <p class="text-sm text-gray-600">{{ t('discountPercentageOptionsDescription') }}</p>
      <MultipleValuesListField
        name="discountPercentageOptions"
        :button-text="t('addDiscountOption')"
        :field-settings="percentageFieldSettings"
        show-delete-alert
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import MultipleValuesListField, {
  type FieldSettings,
} from '@/components/MultipleValuesListField.vue';
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';
import Divider from '@/components/Divider.vue';

const { t } = useI18n();

const percentageFieldSettings = computed<FieldSettings[]>(() => [
  {
    name: 'value',
    label: t('percentage'),
    type: 'number',
    min: 0,
    max: 100,
    maxFractionDigits: 2,
    adornment: '%',
  },
  {
    name: 'label',
    label: t('label'),
    type: 'text',
    placeholder: t('labelPlaceholder'),
  },
]);
</script>

<i18n lang="json">
{
  "en": {
    "commissionPercentageOptions": "Commission Percentage Options",
    "commissionPercentageOptionsDescription": "Define commission percentage options that agents can choose from. Each option should include a value and descriptive label.",
    "discountPercentageOptions": "Discount Percentage Options",
    "discountPercentageOptionsDescription": "Define discount percentage options that companies can choose from. Each option should include a value and descriptive label.",
    "addCommissionOption": "+ Add Commission Option",
    "addDiscountOption": "+ Add Discount Option",
    "percentage": "Percentage",
    "label": "Label",
    "labelPlaceholder": "e.g., 5 %, approx. 10 bookings / year"
  },
  "de": {
    "commissionPercentageOptions": "Provisions-Prozentsatz-Optionen",
    "commissionPercentageOptionsDescription": "Definieren Sie Provisions-Prozentsatz-Optionen, aus denen Agenten wählen können. Jede Option sollte einen Wert und eine beschreibende Bezeichnung enthalten.",
    "discountPercentageOptions": "Rabatt-Prozentsatz-Optionen",
    "discountPercentageOptionsDescription": "Definieren Sie Rabatt-Prozentsatz-Optionen, aus denen Unternehmen wählen können. Jede Option sollte einen Wert und eine beschreibende Bezeichnung enthalten.",
    "addCommissionOption": "+ Provisions-Option hinzufügen",
    "addDiscountOption": "+ Rabatt-Option hinzufügen",
    "percentage": "Prozentsatz",
    "label": "Bezeichnung",
    "labelPlaceholder": "z.B. 5 %, ca. 10 Buchungen / Jahr"
  },
  "es": {
    "commissionPercentageOptions": "Opciones de porcentaje de comisión",
    "commissionPercentageOptionsDescription": "Define opciones de comisión entre las que pueden elegir los agentes. Cada opción debe incluir un valor y una etiqueta descriptiva.",
    "discountPercentageOptions": "Opciones de porcentaje de descuento",
    "discountPercentageOptionsDescription": "Define opciones de descuento entre las que pueden elegir las empresas. Cada opción debe incluir un valor y una etiqueta descriptiva.",
    "addCommissionOption": "+ Añadir opción de comisión",
    "addDiscountOption": "+ Añadir opción de descuento",
    "percentage": "Porcentaje",
    "label": "Etiqueta",
    "labelPlaceholder": "p. ej. 5 %, unas 10 reservas / año"
  }
}
</i18n>
